/**
 * EditPricingTiersModal — band (tier) editor for a plan's pricing tiers.
 * Rows: minUsers, maxUsers (empty = unbounded top band), price, currency.
 * Validation: contiguous, non-overlapping, only last band may be unbounded,
 * minUsers must be ascending.
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Plus, Trash2, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import planService, { Plan, PricingTierInput } from '../../services/api/planService';
import { extractApiError } from '../../utils/apiError';
import type { PlanPricingTier } from '@upskill/shared';

// ============================================================================
// Types
// ============================================================================

interface TierRow {
  /** Existing tier id — undefined for new rows */
  id?: string;
  minUsers: string;
  maxUsers: string; // empty string = unbounded
  price: string;
  currency: string;
}

interface EditPricingTiersModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: Plan;
}

// ============================================================================
// Helpers
// ============================================================================

function tierToRow(tier: PlanPricingTier): TierRow {
  return {
    id: tier.id,
    minUsers: String(tier.minUsers),
    maxUsers: tier.maxUsers != null ? String(tier.maxUsers) : '',
    price: String(tier.price),
    currency: tier.currency,
  };
}

type ValidationKey =
  | { key: 'billing.pricingTiers.validation.atLeastOne' }
  | { key: 'billing.pricingTiers.validation.minUsersInvalid'; row: number }
  | { key: 'billing.pricingTiers.validation.priceInvalid'; row: number }
  | { key: 'billing.pricingTiers.validation.currencyRequired'; row: number }
  | { key: 'billing.pricingTiers.validation.onlyLastUnbounded' }
  | { key: 'billing.pricingTiers.validation.maxUsersMustBePositive'; row: number }
  | { key: 'billing.pricingTiers.validation.maxMustExceedMin'; row: number }
  | { key: 'billing.pricingTiers.validation.minMustAscend'; row: number }
  | { key: 'billing.pricingTiers.validation.notContiguous'; row: number; min: number; expected: number };

function validateRows(rows: TierRow[]): ValidationKey | null {
  // An empty array is valid — it clears all tiers from the plan (supported by the backend).
  if (rows.length === 0) return null;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const min = Number(row.minUsers);
    const price = Number(row.price);

    if (!row.minUsers || isNaN(min) || min < 0) {
      return { key: 'billing.pricingTiers.validation.minUsersInvalid', row: i + 1 };
    }
    if (!row.price || isNaN(price) || price < 0) {
      return { key: 'billing.pricingTiers.validation.priceInvalid', row: i + 1 };
    }
    if (!row.currency.trim()) {
      return { key: 'billing.pricingTiers.validation.currencyRequired', row: i + 1 };
    }

    // Only the last row can be unbounded
    if (row.maxUsers === '' && i < rows.length - 1) {
      return { key: 'billing.pricingTiers.validation.onlyLastUnbounded' };
    }

    if (row.maxUsers !== '') {
      const max = Number(row.maxUsers);
      if (isNaN(max) || max <= 0) {
        return { key: 'billing.pricingTiers.validation.maxUsersMustBePositive', row: i + 1 };
      }
      if (max <= min) {
        return { key: 'billing.pricingTiers.validation.maxMustExceedMin', row: i + 1 };
      }
    }

    // Check ascending minUsers
    if (i > 0) {
      const prevMin = Number(rows[i - 1].minUsers);
      if (!isNaN(prevMin) && min <= prevMin) {
        return { key: 'billing.pricingTiers.validation.minMustAscend', row: i + 1 };
      }
      // Check contiguity: this row's minUsers should equal previous maxUsers + 1
      const prevMax = rows[i - 1].maxUsers;
      if (prevMax !== '') {
        const prevMaxNum = Number(prevMax);
        if (!isNaN(prevMaxNum) && min !== prevMaxNum + 1) {
          return { key: 'billing.pricingTiers.validation.notContiguous', row: i + 1, min, expected: prevMaxNum + 1 };
        }
      }
    }
  }

  return null;
}

function rowsToInput(rows: TierRow[]): PricingTierInput[] {
  // NOTE: do NOT send `id` — the backend replace-all endpoint rejects unknown
  // fields (whitelist/forbidNonWhitelisted) and recreates tiers fresh anyway.
  return rows.map((row) => ({
    minUsers: Number(row.minUsers),
    maxUsers: row.maxUsers === '' ? null : Number(row.maxUsers),
    price: Number(row.price),
    currency: row.currency.trim(),
  }));
}

// ============================================================================
// Component
// ============================================================================

export const EditPricingTiersModal = ({ isOpen, onClose, plan }: EditPricingTiersModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['plan-pricing-tiers', plan.id], [plan.id]);

  const [rows, setRows] = useState<TierRow[]>([]);
  const [validationError, setValidationError] = useState<ValidationKey | null>(null);
  const [successMessage, setSuccessMessage] = useState<boolean>(false);

  const { data: currentTiers, isLoading } = useQuery({
    queryKey,
    queryFn: () => planService.getPricingTiers(plan.id),
    enabled: isOpen,
  });

  useEffect(() => {
    if (currentTiers) {
      setRows(currentTiers.length > 0 ? currentTiers.map(tierToRow) : [defaultRow()]);
    }
  }, [currentTiers]);

  useEffect(() => {
    setValidationError(null);
    setSuccessMessage(false);
  }, [isOpen, plan.id]);

  const saveMutation = useMutation({
    mutationFn: (tiers: PricingTierInput[]) => planService.setPricingTiers(plan.id, tiers),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setSuccessMessage(true);
    },
  });

  const handleClose = () => {
    saveMutation.reset();
    setValidationError(null);
    setSuccessMessage(false);
    onClose();
  };

  const handleSave = () => {
    setValidationError(null);
    setSuccessMessage(false);
    const error = validateRows(rows);
    if (error) {
      setValidationError(error);
      return;
    }
    saveMutation.mutate(rowsToInput(rows));
  };

  const addRow = () => {
    setValidationError(null);
    setSuccessMessage(false);
    setRows((prev) => {
      // Suggest minUsers = previous maxUsers + 1 for convenience
      const last = prev[prev.length - 1];
      const suggestedMin =
        last && last.maxUsers !== ''
          ? String(Number(last.maxUsers) + 1)
          : '';
      return [
        ...prev,
        { minUsers: suggestedMin, maxUsers: '', price: '', currency: 'USD' },
      ];
    });
  };

  const removeRow = (index: number) => {
    setValidationError(null);
    setSuccessMessage(false);
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof TierRow, value: string) => {
    setValidationError(null);
    setSuccessMessage(false);
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  // The ValidationKey union carries the i18n key and all interpolation params as own fields.
  // Spread directly into t() — no switch needed.
  const validationMessage = validationError
    ? t(validationError.key, validationError as Record<string, unknown>)
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('billing.pricingTiers.modalTitle', { planName: plan.name })}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={saveMutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary-blue"
            onClick={handleSave}
            disabled={saveMutation.isPending || isLoading}
            isLoading={saveMutation.isPending}
          >
            {t('billing.pricingTiers.saveButton')}
          </Button>
        </>
      }
    >
      <ModalBody>
        {saveMutation.isError && (
          <FeedbackMessage $variant="error">
            {extractApiError(saveMutation.error, t('billing.pricingTiers.saveFailed'))}
          </FeedbackMessage>
        )}

        {validationMessage && (
          <FeedbackMessage $variant="error">{validationMessage}</FeedbackMessage>
        )}

        {successMessage && (
          <FeedbackMessage $variant="success">{t('billing.pricingTiers.savedSuccess')}</FeedbackMessage>
        )}

        <HelperText>{t('billing.pricingTiers.helperText')}</HelperText>

        {isLoading ? (
          <LoadingText>{t('billing.pricingTiers.loadingTiers')}</LoadingText>
        ) : (
          <>
            <TiersTable>
              <TiersHead>
                <tr>
                  <Th>{t('billing.pricingTiers.colMinUsers')}</Th>
                  <Th>{t('billing.pricingTiers.colMaxUsers')}</Th>
                  <Th>{t('billing.pricingTiers.colPrice')}</Th>
                  <Th>{t('billing.pricingTiers.colCurrency')}</Th>
                  <Th />
                </tr>
              </TiersHead>
              <TiersBody>
                {rows.length === 0 ? (
                  <tr>
                    <EmptyTd colSpan={5}>{t('billing.pricingTiers.noTiersDefined')}</EmptyTd>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={index}>
                      <Td>
                        <TierInput
                          type="number"
                          min="0"
                          placeholder={t('billing.pricingTiers.minUsersPlaceholder')}
                          value={row.minUsers}
                          onChange={(e) => updateRow(index, 'minUsers', e.target.value)}
                          disabled={saveMutation.isPending}
                          aria-label={t('billing.pricingTiers.ariaMinUsers', { n: index + 1 })}
                        />
                      </Td>
                      <Td>
                        <TierInput
                          type="number"
                          min="1"
                          placeholder={t('billing.pricingTiers.maxUsersPlaceholder')}
                          value={row.maxUsers}
                          onChange={(e) => updateRow(index, 'maxUsers', e.target.value)}
                          disabled={saveMutation.isPending}
                          aria-label={t('billing.pricingTiers.ariaMaxUsers', { n: index + 1 })}
                        />
                      </Td>
                      <Td>
                        <TierInput
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={t('billing.pricingTiers.pricePlaceholder')}
                          value={row.price}
                          onChange={(e) => updateRow(index, 'price', e.target.value)}
                          disabled={saveMutation.isPending}
                          aria-label={t('billing.pricingTiers.ariaPrice', { n: index + 1 })}
                        />
                      </Td>
                      <Td>
                        <TierInput
                          type="text"
                          maxLength={3}
                          placeholder={t('billing.pricingTiers.currencyPlaceholder')}
                          value={row.currency}
                          onChange={(e) => updateRow(index, 'currency', e.target.value.toUpperCase())}
                          disabled={saveMutation.isPending}
                          aria-label={t('billing.pricingTiers.ariaCurrency', { n: index + 1 })}
                        />
                      </Td>
                      <Td $action>
                        <RemoveButton
                          type="button"
                          onClick={() => removeRow(index)}
                          disabled={saveMutation.isPending}
                          aria-label={t('billing.pricingTiers.ariaRemoveTier', { n: index + 1 })}
                          title={t('billing.pricingTiers.titleRemoveTier')}
                        >
                          <Trash2 size={14} />
                        </RemoveButton>
                      </Td>
                    </tr>
                  ))
                )}
              </TiersBody>
            </TiersTable>

            <AddTierRow>
              <Button
                variant="outlined"
                size="sm"
                leftIcon={<Plus />}
                onClick={addRow}
                disabled={saveMutation.isPending}
              >
                {t('billing.pricingTiers.addTierButton')}
              </Button>
              <HintText>
                <DollarSign size={12} />
                {t('billing.pricingTiers.hintText')}
              </HintText>
            </AddTierRow>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

function defaultRow(): TierRow {
  return { minUsers: '1', maxUsers: '', price: '', currency: 'USD' };
}

export default EditPricingTiersModal;

// ============================================================================
// Styled Components
// ============================================================================

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const FeedbackMessage = styled.div<{ $variant: 'error' | 'success' }>`
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  ${({ $variant, theme }) =>
    $variant === 'error'
      ? `
    background: ${theme.colors.status.error.bg};
    border: 1px solid ${theme.colors.status.error.border};
    color: ${theme.colors.status.error.text};
  `
      : `
    background: ${theme.colors.status.success.bg};
    border: 1px solid ${theme.colors.status.success.border};
    color: ${theme.colors.status.success.text};
  `}
`;

const HelperText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TiersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const TiersHead = styled.thead`
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const TiersBody = styled.tbody``;

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: left;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const Td = styled.td<{ $action?: boolean }>`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  vertical-align: middle;
  width: ${({ $action }) => ($action ? '40px' : 'auto')};

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.borders.light}40;
  }
`;

const EmptyTd = styled.td`
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const TierInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  height: 36px;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.secondary};
    cursor: not-allowed;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.status.error.text};
  background: none;
  border: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.status.error.bg};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const AddTierRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const HintText = styled.p`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[1]};
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  line-height: 1.5;
  flex: 1;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${({ theme }) => theme.colors.text.light};
  }
`;
